import {gql, MutationFunctionOptions, MutationUpdaterFn, useMutation, useQuery} from '@apollo/client'
import {LinkPhoto, LinkPhotoVariables} from './__types__/LinkPhoto'
import {UnlinkPhoto, UnlinkPhotoVariables} from './__types__/UnlinkPhoto'
import {UnlinkPhotos, UnlinkPhotosVariables} from './__types__/UnlinkPhotos'
import {DeletePhoto, DeletePhotoVariables} from './__types__/DeletePhoto'
import {CropAndRotate, CropAndRotateVariables} from './__types__/CropAndRotate'
import {DeletePhotos, DeletePhotosVariables} from './__types__/DeletePhotos'
import {UploadPhoto, UploadPhotoVariables} from './__types__/UploadPhoto'
import {ExecutionResult} from 'graphql'
import {PhotosData, PhotosDataVariables} from './__types__/PhotosData'
import {DownloadPhotosReport, DownloadPhotosReportVariables} from './__types__/DownloadPhotosReport'
import {getGraphQLErrorMessage} from '~/util'
import {useAlert} from 'react-alert'
import {DownloadPhotos, DownloadPhotosVariables} from './__types__/DownloadPhotos'
import produce from 'immer'
import {cloneDeep} from 'lodash'

export const PHOTOS_DATA = gql`
    query PhotosData($organisationIdStr: String!) {
        organisation(idStr: $organisationIdStr) {
            idStr
            fields {
                idStr
                label
            }
            profiles {
                idStr
                fullName
                photoRequested
                createdAt
                photos {
                    idStr
                    thumbUrl
                    profileUrl
                    originalUrl
                    priority
                }
            }
            photos {
                idStr
                filename
                thumbUrl
                priority
                originalUrl
                createdAt
                profile {
                    idStr
                    fullName
                }
                pendingProfile {
                    idStr
                    fullName
                }
            }
        }
    }
`

/**
 * Gets the refetch query for photos data
 *
 * @export
 * @param {string} organisationIdStr
 * @returns
 */
export const getPhotosDataQuery = (organisationIdStr: string) => {
    return {
        query: PHOTOS_DATA,
        variables: {
            organisationIdStr,
        },
    }
}

export const usePhotosData = (organisationIdStr: string) =>
    useQuery<PhotosData, PhotosDataVariables>(PHOTOS_DATA, {
        variables: {
            organisationIdStr,
        },
    })

export const UPLOAD_PHOTO = gql`
    mutation UploadPhoto($imgFile: Upload!, $organisationIdStr: String!) {
        uploadPhoto(imgFile: $imgFile, organisationIdStr: $organisationIdStr) {
            idStr
            filename
            thumbUrl
            originalUrl
            profileUrl
            priority
            createdAt
            profile {
                idStr
                fullName
                firstName
                email
                photoRequested
            }
            pendingProfile {
                idStr
                fullName
            }
        }
    }
`

export const useUploadPhoto = (organisationIdStr: string) =>
    useMutation<UploadPhoto, UploadPhotoVariables>(UPLOAD_PHOTO, {
        update: (cache, {data}) => {
            if (!data) return
            if (!data.uploadPhoto) return

            const getPhotosResult = cache.readQuery<PhotosData, PhotosDataVariables>({
                query: PHOTOS_DATA,
                variables: {
                    organisationIdStr,
                },
            })

            if (!getPhotosResult) return

            cache.writeQuery<PhotosData, PhotosDataVariables>({
                query: PHOTOS_DATA,
                data: {
                    organisation: {
                        ...getPhotosResult.organisation,
                        photos: getPhotosResult.organisation.photos.concat([data.uploadPhoto]),
                    },
                },
            })
        },
    })

export type UploadPhotoMutationFn = (
    options?: MutationFunctionOptions<UploadPhoto, UploadPhotoVariables> | undefined,
) => Promise<void | ExecutionResult<UploadPhoto>>

export const LINK_PHOTO = gql`
    mutation LinkPhoto($photoIdStr: String!, $profileIdStr: String!) {
        linkPhotoToProfile(photoIdStr: $photoIdStr, profileIdStr: $profileIdStr) {
            photos {
                idStr
                profile {
                    idStr
                }
            }
            profiles {
                idStr
                photos {
                    idStr
                }
            }
        }
    }
`

export const useLinkPhoto = () => useMutation<LinkPhoto, LinkPhotoVariables>(LINK_PHOTO)

export const UNLINK_PHOTO = gql`
    mutation UnlinkPhoto($photoIdStr: String!) {
        unlinkPhoto(photoIdStr: $photoIdStr) {
            photo {
                idStr
                profile {
                    idStr
                }
            }
            profile {
                idStr
                photos {
                    idStr
                }
            }
        }
    }
`

export const useUnlinkPhoto = () => useMutation<UnlinkPhoto, UnlinkPhotoVariables>(UNLINK_PHOTO)

export const UNLINK_PHOTOS = gql`
    mutation UnlinkPhotos($photoIdStrs: [String!]!) {
        unlinkPhotos(photoIdStrs: $photoIdStrs) {
            photos {
                idStr
                profile {
                    idStr
                }
            }
            profiles {
                idStr
                photos {
                    idStr
                }
            }
        }
    }
`

export const useUnlinkPhotos = ({photoIdStrs}: {photoIdStrs: string[]}) =>
    useMutation<UnlinkPhotos, UnlinkPhotosVariables>(UNLINK_PHOTOS, {variables: {photoIdStrs}})

export const DOWNLOAD_PHOTOS_REPORT = gql`
    mutation DownloadPhotosReport($organisationIdStr: String!) {
        downloadPhotosReport(organisationIdStr: $organisationIdStr)
    }
`

export const useDownloadPhotosReport = () => {
    const alert = useAlert()
    return useMutation<DownloadPhotosReport, DownloadPhotosReportVariables>(DOWNLOAD_PHOTOS_REPORT, {
        onError: (error) => {
            alert.error(getGraphQLErrorMessage(error))
            throw error
        },
    })
}

export const DOWNLOAD_PHOTOS = gql`
    mutation DownloadPhotos($organisationIdStr: String!) {
        downloadPhotos(organisationIdStr: $organisationIdStr)
    }
`

export const useDownloadPhotos = () => {
    const alert = useAlert()
    return useMutation<DownloadPhotos, DownloadPhotosVariables>(DOWNLOAD_PHOTOS, {
        onError: (error) => {
            alert.error(getGraphQLErrorMessage(error))
            throw error
        },
    })
}

export const DELETE_PHOTO = gql`
    mutation DeletePhoto($photoIdStr: String!) {
        deletePhoto(photoIdStr: $photoIdStr) {
            photo {
                idStr
            }
        }
    }
`

export type DeletePhotoMutationUpdater = MutationUpdaterFn<DeletePhoto>

export const useDeletePhoto = ({photoIdStr, update}: {photoIdStr: string; update: MutationUpdaterFn<DeletePhoto>}) => {
    return useMutation<DeletePhoto, DeletePhotoVariables>(DELETE_PHOTO, {
        variables: {
            photoIdStr,
        },
        update,
    })
}

export const deletePhotoUpdate: (organisationIdStr: string) => DeletePhotoMutationUpdater = (
    organisationIdStr: string,
) => (cache, {data}) => {
    if (!data || !data.deletePhoto || !data.deletePhoto.photo) return
    const idStr = data.deletePhoto.photo.idStr
    const getPhotosResult = cache.readQuery<PhotosData, PhotosDataVariables>({
        query: PHOTOS_DATA,
        variables: {organisationIdStr},
    })
    if (!getPhotosResult) return

    cache.writeQuery<PhotosData, PhotosDataVariables>({
        query: PHOTOS_DATA,
        data: produce(cloneDeep(getPhotosResult), (draftState) => {
            draftState.organisation.photos = draftState.organisation.photos.filter((photo) => photo.idStr !== idStr)
            draftState.organisation.profiles.forEach((profile) => {
                if (profile.photos?.[0]) {
                    profile.photos = profile.photos.filter((photo) => photo.idStr !== idStr)
                }
            })
        }),
        variables: {organisationIdStr},
    })
}

export const DELETE_PHOTOS = gql`
    mutation DeletePhotos($photoIdStrs: [String!]!) {
        deletePhotos(photoIdStrs: $photoIdStrs) {
            photos {
                idStr
            }
        }
    }
`

export type DeletePhotosMutationUpdater = MutationUpdaterFn<DeletePhotos>
export interface UseDeletePhotosProps {
    photoIdStrs: string[]
    update: DeletePhotosMutationUpdater | undefined
}
export const useDeletePhotos = ({photoIdStrs, update}: UseDeletePhotosProps) => {
    return useMutation<DeletePhotos, DeletePhotosVariables>(DELETE_PHOTOS, {
        variables: {
            photoIdStrs,
        },
        update,
    })
}

export const deletePhotosUpdate: (organisationIdStr: string) => DeletePhotosMutationUpdater = (
    organisationIdStr: string,
) => (cache, {data}) => {
    if (!data || !data.deletePhotos || !data.deletePhotos.photos) return
    const deletedPhotoIds = data.deletePhotos.photos.map((photo) => photo.idStr)

    const getPhotosResult = cache.readQuery<PhotosData, PhotosDataVariables>({
        query: PHOTOS_DATA,
        variables: {organisationIdStr},
    })
    if (!getPhotosResult) return

    cache.writeQuery<PhotosData, PhotosDataVariables>({
        query: PHOTOS_DATA,
        data: produce(cloneDeep(getPhotosResult), (draftState) => {
            draftState.organisation.photos = draftState.organisation.photos.filter(
                (photo) => !deletedPhotoIds.includes(photo.idStr),
            )

            draftState.organisation.profiles.forEach((profile) => {
                if (profile.photos?.[0]) {
                    profile.photos = profile.photos.filter((photo) => !deletedPhotoIds.includes(photo.idStr))
                }
            })
        }),
        variables: {organisationIdStr},
    })
}

export const CROP_AND_ROTATE = gql`
    mutation CropAndRotate($photoIdStr: String!, $cropAndRotate: CropAndRotateInput!) {
        cropAndRotate(photoIdStr: $photoIdStr, cropAndRotate: $cropAndRotate) {
            idStr
            originalUrl
            thumbUrl
            profileUrl
            priority
        }
    }
`

export const useCropAndRotate = () => {
    return useMutation<CropAndRotate, CropAndRotateVariables>(CROP_AND_ROTATE)
}
